/**
 * OAuth Buttons Component
 *
 * Displays sign-in buttons for all configured OAuth providers:
 * - Google
 * - Apple
 * - Microsoft
 * - Yahoo
 */

import type { OAuthProviderType } from '../../config/firebase';

interface OAuthButtonsProps {
  onOAuthClick: (provider: OAuthProviderType) => Promise<void>;
  disabled?: boolean;
}

export default function OAuthButtons({ onOAuthClick, disabled }: OAuthButtonsProps) {
  const providers: Array<{
    type: OAuthProviderType;
    name: string;
    icon: React.ReactNode;
    bgClass: string;
  }> = [
    {
      type: 'google',
      name: 'Google',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      ),
      bgClass: 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300',
    },
    {
      type: 'apple',
      name: 'Apple',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      ),
      bgClass: 'bg-black hover:bg-gray-900 text-white',
    },
    // Microsoft and Yahoo OAuth removed - will be re-added if Windows PC app is created
    // {
    //   type: 'microsoft',
    //   name: 'Microsoft',
    //   icon: (
    //     <svg className="w-5 h-5" viewBox="0 0 24 24">
    //       <path fill="#F25022" d="M1 1h10v10H1z" />
    //       <path fill="#00A4EF" d="M1 13h10v10H1z" />
    //       <path fill="#7FBA00" d="M13 1h10v10H13z" />
    //       <path fill="#FFB900" d="M13 13h10v10H13z" />
    //     </svg>
    //   ),
    //   bgClass: 'bg-[#2F2F2F] hover:bg-[#1F1F1F] text-white',
    // },
    // {
    //   type: 'yahoo',
    //   name: 'Yahoo',
    //   icon: (
    //     <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    //       <path d="M12.913 12.596l4.557-9.596h-3.107l-2.36 5.382-2.363-5.382h-3.107l4.557 9.596v6.404h1.823v-6.404zm7.087-9.596h-3v3h3v-3zm-3 15h3v3h-3v-3z" />
    //     </svg>
    //   ),
    //   bgClass: 'bg-[#720E9E] hover:bg-[#5A0B7D] text-white',
    // },
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {providers.map((provider) => (
        <button
          key={provider.type}
          type="button"
          onClick={() => onOAuthClick(provider.type)}
          disabled={disabled}
          className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed ${provider.bgClass}`}
        >
          {provider.icon}
          <span className="hidden sm:inline">{provider.name}</span>
        </button>
      ))}
    </div>
  );
}
